import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchProfessionalInvoices, fetchProfessionalInvoice, confirmProfessionalInvoice } from '../services/invoiceApi';
import { Card, Button, CreateInvoiceForm } from '../components';
import { FileText, Plus, Download, Info, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { ContractAddendumPopup } from '../components/ContractAddendumPopup';

const ProfessionalInvoicesPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const queryClient = useQueryClient();
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);

  const confirmMutation = useMutation({
    mutationFn: (invoiceId: string) => confirmProfessionalInvoice(invoiceId),
    onSuccess: (res) => {
      const naar: string | null = res.data?.data?.verstuurd_naar ?? null;
      toast.success(t('invoices.performanceReported'));
      // Na gereedmelden mailt de server de factuur automatisch naar de opdrachtgever
      if (naar) toast.success(t('invoices.automatischVerstuurd', { email: naar, defaultValue: 'Factuur verstuurd naar {{email}}' }));
      else toast.error(t('invoices.automatischMislukt', 'De factuur kon niet automatisch gemaild worden. Gebruik "Versturen".'), { duration: 8000 });
      setDeclarationChecked(false);
      queryClient.invalidateQueries({ queryKey: ['professional-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['professional-invoice', selectedInvoice] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('invoices.reportError'));
    },
  });

  const sendMutation = useMutation({
    mutationFn: (invoiceId: string) => api.post(`/professional/invoices/${invoiceId}/send`),
    onSuccess: (_, invoiceId) => {
      toast.success('Factuur verstuurd naar opdrachtgever');
      queryClient.invalidateQueries({ queryKey: ['professional-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['professional-invoice', invoiceId] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Fout bij versturen factuur');
    },
  });

  // Fetch invoices
  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['professional-invoices'],
    queryFn: async () => {
      const res = await fetchProfessionalInvoices();
      // Handle both old format (array) and new format ({ success: true, data: [...] })
      if (Array.isArray(res.data)) {
        return res.data;
      }
      return res.data?.data || [];
    }
  });

  // Fetch selected invoice details
  const { data: invoiceDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['professional-invoice', selectedInvoice],
    queryFn: async () => {
      if (!selectedInvoice) return null;
      const res = await fetchProfessionalInvoice(selectedInvoice);
      return res.data;
    },
    enabled: !!selectedInvoice
  });

  // Fetch active employers
  const { data: employers } = useQuery({
    queryKey: ['active-employers'],
    queryFn: async () => {
      const response = await api.get('/professional/active-employers');
      return response.data.data || [];
    }
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Gereedmelden is de urensluiting: entiteitsaddendum (besluit 14) */}
      <ContractAddendumPopup context="uren" />
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoices.title')}</h1>
          <p className="mt-1 text-gray-600">{t('invoices.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('cockpit.newInvoice')}
        </Button>
      </div>

      {/* Create Invoice Form */}
      {showCreateForm && (
        <CreateInvoiceForm
          employers={employers || []}
          onSuccess={(invoiceId) => {
            setShowCreateForm(false);
            setSelectedInvoice(invoiceId);
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* Invoices List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('sidebar.invoices')}</h2>
            {invoicesLoading ? (
              <div className="text-center py-8 text-gray-500">{t('invoices.loading')}</div>
            ) : invoices && invoices.length > 0 ? (
              <div className="space-y-2">
                {invoices.map((inv: any) => (
                  <div
                    key={inv.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedInvoice === inv.id ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => { setSelectedInvoice(inv.id); setDeclarationChecked(false); }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{inv.invoice_number}</div>
                        <div className="text-sm text-gray-500">
                          {inv.invoice_year}-{String(inv.invoice_month).padStart(2, '0')}
                        </div>
                        <div className="text-sm font-semibold text-gray-900 mt-1">
                          &euro;{parseFloat(inv.total_amount).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        {inv.status === 'draft' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {t('invoices.draft')}
                          </span>
                        )}
                        {inv.status === 'sent' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('invoices.sent')}
                          </span>
                        )}
                        {inv.indieningswijze === 'stilzwijgende_fiat' && (
                          <span className="block mt-1 text-xs text-gray-500" title={inv.fiat_grondslag ?? undefined}>{t('invoices.autoIngediend')}</span>
                        )}
                        {inv.status === 'paid' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('cockpit.paid')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>{t('invoices.noInvoicesYet')}</p>
              </div>
            )}
          </Card>
        </div>

        <div className="lg:col-span-2">
          {selectedInvoice && invoiceDetails ? (
            <Card>
              {detailsLoading ? (
                <div className="text-center py-8 text-gray-500">{t('invoices.loading')}</div>
              ) : (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold">{invoiceDetails.invoice_number}</h2>
                      <p className="text-gray-600 mt-1">
                        {t('invoices.period')}: {new Date(invoiceDetails.period_start).toLocaleDateString('nl-NL')} - {new Date(invoiceDetails.period_end).toLocaleDateString('nl-NL')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`/api/invoices/professional/${selectedInvoice}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button variant="outline">
                          <Download className="w-4 h-4 mr-2" />
                          {t('invoices.downloadPdf')}
                        </Button>
                      </a>
                      {invoiceDetails.status !== 'paid' && (
                        <Button
                          onClick={() => sendMutation.mutate(selectedInvoice!)}
                          disabled={sendMutation.isPending}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {sendMutation.isPending ? 'Versturen...' : invoiceDetails.status === 'sent' ? 'Opnieuw sturen' : 'Stuur factuur'}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">{t('invoices.totalHours')}</div>
                      <div className="text-2xl font-bold">{invoiceDetails.total_hours}</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600">{t('invoices.totalAmount')}</div>
                      <div className="text-2xl font-bold">&euro;{parseFloat(invoiceDetails.total_amount).toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold mb-4">{t('invoices.invoiceLines')}</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.date')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.hours')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.travelTime')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.km')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.parking')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.totalCol')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(invoiceDetails.lines || []).map((line: any) => (
                            <tr key={line.id}>
                              <td className="px-4 py-3 text-sm">
                                {new Date(line.datum_afspraak || line.work_date).toLocaleDateString('nl-NL')}
                              </td>
                              <td className="px-4 py-3 text-sm">{line.uren || line.hours}</td>
                              <td className="px-4 py-3 text-sm">
                                {line.reistijdvergoeding_uren || line.reistijd_uren ? (
                                  <div>
                                    <div className="text-xs text-gray-500">
                                      {parseFloat(line.reistijdvergoeding_uren || line.reistijd_uren || 0).toFixed(2)} {t('invoices.hoursUnit')}
                                    </div>
                                    <div className="font-medium">
                                      &euro;{parseFloat(line.reistijdvergoeding_bedrag || 0).toFixed(2)}
                                    </div>
                                  </div>
                                ) : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {line.reiskosten_bedrag ? `\u20AC${parseFloat(line.reiskosten_bedrag).toFixed(2)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {line.parkeerkosten ? `\u20AC${parseFloat(line.parkeerkosten).toFixed(2)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">
                                &euro;{parseFloat(line.line_total || line.line_amount).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {invoiceDetails.status === 'draft' && (
                    <div className="border-t mt-6 pt-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                        <h3 className="text-base font-semibold text-blue-900 mb-4">{t('invoices.reportPerformance')}</h3>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={declarationChecked}
                            onChange={(e) => setDeclarationChecked(e.target.checked)}
                          />
                          <span className="text-sm text-blue-900">
                            {t('invoices.declarationText')}
                          </span>
                        </label>
                        <div className="mt-4 flex items-start gap-3">
                          <div className="relative group">
                            <Button
                              onClick={() => confirmMutation.mutate(selectedInvoice!)}
                              disabled={!declarationChecked || confirmMutation.isPending}
                            >
                              {confirmMutation.isPending ? t('invoices.processing') : t('invoices.reportPerformanceBtn')}
                            </Button>
                            <div className="absolute bottom-full left-0 mb-2 w-80 bg-gray-800 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <div className="flex gap-2">
                                <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-300" />
                                <p>{t('invoices.prestatieTooltip')}</p>
                              </div>
                              <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800" />
                            </div>
                          </div>
                        </div>
                        <p className="mt-3 text-xs text-blue-700 italic">
                          <Info className="inline w-3 h-3 mr-1" />
                          {t('invoices.whyPerformance')}
                        </p>
                        <p className="mt-2 text-xs text-blue-700">{t('invoices.autoIndieningUitleg')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ) : (
            <Card>
              <div className="text-center py-12 text-gray-500">
                <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p>{t('invoices.selectInvoice')}</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalInvoicesPage;
